import { useCallback } from 'react';
import { Invitation } from 'sip.js';
import { useSoftphoneCallState } from '..';
import { IncomingCall } from '../../../types';
import { useSoftphoneClient } from '../../softphone-client-provider';
import { useSoftphoneEventsEmitter } from '../../softphone-events-provider';
import { useSoftphoneSettings } from '../../softphone-settings-provider';

export const useHandleIncomingCall = () => {
  const { emit } = useSoftphoneEventsEmitter();
  const addCall = useSoftphoneCallState((ctx) => ctx.addCall);
  const calls = useSoftphoneCallState((ctx) => ctx.calls);
  const client = useSoftphoneClient((ctx) => ctx.client);
  const mergedCallGroup = useSoftphoneCallState((ctx) => ctx.mergedCallGroup);
  const isDoNotDisturb = useSoftphoneSettings((ctx) => ctx.isDoNotDisturb);

  return useCallback(
    async (invitation: Invitation) => {
      const call = {
        id: invitation.id,
        invitation: invitation,
        receivedAt: new Date(),
        onHoldSince: undefined,
        remoteParty: {
          displayName: invitation.remoteIdentity.displayName || invitation.remoteIdentity.displayName || undefined,
          uri: invitation.remoteIdentity.uri.user ?? 'unknown',
        },
      } satisfies IncomingCall;

      /* We place this reject here because if we handle this in call-pop-manager, the call is added and the ringing audio is wonky 
          after setting isDoNotDisturb to false and accepting an incoming call
      */

      if (isDoNotDisturb) {
        invitation.reject({
          statusCode: 486,
          reasonPhrase: 'Busy Here - Do Not Disturb Enabled',
        });
        invitation.dispose();
        emit('incoming-call.rejected', call);
        return;
      }

      addCall(call);
      //Note: since the call pop is the only way to answer an incoming call, we're going to defer the audio to the call pop.
      //This will ensure that the audio can only be played if the call pop is being displayed.
      // play.incomingCall();
      emit('incoming-call.received', call);
      call.invitation._onCancel = (message) => {
        const match = message.headers['Reason'][0].raw.match(/text="([^"]+)"/);
        if (match?.[1] === 'User ends call') emit('incoming-call.remote.canceled', call);
        if (match?.[1] === 'Call completed elsewhere') emit('incoming-call.completed-elsewhere', call);
        // https://groups.google.com/g/sip_js/c/lnWRlZAmm04
        call.invitation.dispose();
      };
      return call;
    },
    [calls, client, addCall, mergedCallGroup, isDoNotDisturb]
  );
};
